import { defineComponent as _defineComponent } from 'vue'
import { inject, onMounted, PropType, ref } from "vue";
import { IBusStopData } from "@/domain/entities/busStop";
import { ILineInfoLite } from "@/domain/entities/lineInfo";
import InlineSvg from "vue-inline-svg";
import { nextTick } from "process";
import { useFavoriteBusStop } from "@/composables/busStop/useFavoriteBusStop";
import { useLineStore } from "@/stores/lineStore";
import {
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowErrorModal,
  ShowToastMessage,
} from "@/types";
import Modal from "../shared/modal/base/Modal.vue";
import { IFavoriteBusStop } from "@/domain/entities/favoriteBusStop";
import useGetLineRouteName from "@/use/useGetLineRouteName";


const __default__ = {
  name: "BusStopInfoSimple",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  busStop: {
    type: Object as PropType<IBusStopData>,
    required: true,
  },
  lineInfos: {
    type: Array as PropType<ILineInfoLite[]>,
    required: true,
  },
},
  emits: ["showAllLineInfoList"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const emit = __emit;

const lineStore = useLineStore();

const { isFavorite } = useFavoriteBusStop();

// 텍스트를 렌더링할 영역의 컨테이너 ref
const linesInfo = ref<HTMLElement | null>(null);
// 동적으로 계산된 텍스트 렌더링 영역의 최대 너비(픽셀)
const availableWidth = ref<number>(0);

// 노선명 텍스트가 들어갈 수 있는 최대 너비를 계산
const calculateAvailableWidth = () => {
  if (linesInfo.value) {
    const chipWidth = 25;
    const leftMargin = 8;
    const rightMargin = 84;
    availableWidth.value =
      linesInfo.value.clientWidth - chipWidth - leftMargin - rightMargin;
  }
};

// 캔버스를 이용해 텍스트의 픽셀 너비를 측정
const measureTextWidth = (text: string, font = "14px Pretendard"): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (context) {
    context.font = font;
    return context.measureText(text).width;
  }
  return 0;
};

// 주어진 노선 이름 배열과 최대 너비, 폰트를 기준으로 텍스트를 포맷팅
const formatRouteNames = (
  routeNames: string[],
  maxWidth: number,
  font = "14px Pretendard"
): string => {
  if (routeNames.length === 0) return "";
  if (routeNames.length === 1) return routeNames[0]; // routeNames 길이가 1인 경우, 바로 routeName만 반환

  let displayedRoutes: string[] = [];
  let formattedText = "";

  for (let i = 0; i < routeNames.length; i++) {
    displayedRoutes.push(routeNames[i]);
    const remainingCount = routeNames.length - displayedRoutes.length;

    // 남은 노선이 있을 경우 "외 X건" 형식을 포함한 텍스트
    const candidateText =
      remainingCount > 0
        ? displayedRoutes.join(", ") + ` 외 ${remainingCount}건`
        : displayedRoutes.join(", ");

    // 텍스트의 너비를 측정
    const textWidth = measureTextWidth(candidateText, font);
    const adjustValue = 10 - 52; // 텍스트 너비가 약 13px 정도 더 크게 측정되어 보정하는 값. 레이블 너비 52를 빼줌
    if (textWidth <= maxWidth + adjustValue) {
      formattedText = candidateText;
    } else {
      // 현재 노선을 추가하면 최대 너비를 초과하는 경우, 마지막 추가한 노선을 제외
      displayedRoutes.pop();
      const finalRemainingCount = routeNames.length - displayedRoutes.length;
      formattedText =
        displayedRoutes.join(", ") + ` 외 ${finalRemainingCount}건`;
      break;
    }
  }

  return formattedText;
};

const getLineInfosStrOfLineTypeCode = (font = "14px Pretendard"): string => {
  const routeNames = props.lineInfos.map((lineInfo) =>
    useGetLineRouteName(
      lineInfo.lineTypeCode,
      lineInfo.routeName,
      lineInfo.lineNum
    )
  );
  return formatRouteNames(routeNames, availableWidth.value, font);
};

const handleError = (title: string, result: any) => {
  showErrorModal(
    title,
    result.errorMessage,
    result.resultCode,
    result.errorTime
  );
};

const convertToFavorietBusStop = (busStop: IBusStopData): IFavoriteBusStop => {
  const favoriteBusStop: IFavoriteBusStop = {
    uid: busStop.id,
    name: busStop.name,
    shorName: busStop.shortName ?? "",
    lat: busStop.latitude,
    lng: busStop.longitude,
    displayOrder: busStop.order ?? 0,
  };

  return favoriteBusStop;
};

/** 정류장 즐겨찾기 추가 */
const addFavoriteBusStop = async () => {
  const favoriteBusStop = convertToFavorietBusStop(props.busStop);
  const addResult = await lineStore.addFavoriteBusStop(favoriteBusStop);

  if (!addResult.isSuccess) {
    handleError("즐겨찾기 추가 오류", addResult);
    return;
  }

  showToastMessage("즐겨찾기가 추가되었습니다", 2500, 0);
};

/** 정류장 즐겨찾기 삭제 확인 모달 */
const shouldShowAlertToDeleteFavorite = ref(false);
const toggleShowAlertToDeleteFavorite = (show: boolean) => {
  shouldShowAlertToDeleteFavorite.value = show;
};

/** 정류장 즐겨찾기 삭제 */
const removeFavoriteBusStop = async () => {
  const removeResult = await lineStore.removeFavoriteBusStop(props.busStop.id);

  if (!removeResult.isSuccess) {
    handleError("즐겨찾기 삭제 오류", removeResult);
    return;
  }

  showToastMessage("즐겨찾기가 삭제되었습니다", 2500, 0);
};

/** 전체 노선 목록 보기 */
const emitShowAllLineInfoList = () => {
  emit("showAllLineInfoList");
};

onMounted(() => {
  nextTick(() => {
    calculateAvailableWidth();
  });
});

const __returned__ = { props, showErrorModal, showToastMessage, emit, lineStore, isFavorite, linesInfo, availableWidth, calculateAvailableWidth, measureTextWidth, formatRouteNames, getLineInfosStrOfLineTypeCode, handleError, convertToFavorietBusStop, addFavoriteBusStop, shouldShowAlertToDeleteFavorite, toggleShowAlertToDeleteFavorite, removeFavoriteBusStop, emitShowAllLineInfoList, get InlineSvg() { return InlineSvg }, Modal }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})